import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

import type { SearchUsersResponse, UserSummaryResponse } from '@lifetimeline/shared';

import { Field } from '../components/Field.js';
import { Layout } from '../components/Layout.js';
import { PersonList, PersonRow } from '../components/PersonList.js';
import { Spinner } from '../components/Spinner.js';
import { useAuth } from '../features/auth/AuthContext.js';
import { useI18n } from '../i18n/index.js';
import { api } from '../lib/apiClient.js';
import styles from './pageStyles.module.css';

/**
 * 사람 찾기 — 이름이나 주소로 찾는다.
 *
 * 찾는 일만 남긴 화면이다. 팔로우 목록은 "나에 관한 것"이라 내 프로필로
 * 갔다 — 한 화면에 두면 내가 이미 아는 사람과 아직 모르는 사람이 섞인다.
 */
export function PeoplePage() {
  const { t } = useI18n();
  const { status } = useAuth();

  const [query, setQuery] = useState('');
  const [found, setFound] = useState<UserSummaryResponse[] | null>(null);
  /** 보여준 것 말고 **더 있는가.** 있으면 더 좁혀 달라고 말한다 */
  const [hasMore, setHasMore] = useState(false);

  /*
   * 두 글자부터 찾는다. 한 글자로 찾으면 사실상 전체 명부가 되는데,
   * 그건 검색이 아니라 사람 목록을 뿌리는 것이다(서버도 같은 기준으로 막는다).
   */
  useEffect(() => {
    const needle = query.trim();
    if (needle.length < 2) {
      setFound(null);
      setHasMore(false);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      api
        .get<SearchUsersResponse>(`/api/users?q=${encodeURIComponent(needle)}`)
        .then((result) => {
          if (!cancelled) {
            setFound(result.users);
            setHasMore(result.hasMore);
          }
        })
        .catch(() => undefined);
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  if (status === 'loading') {
    return (
      <Layout>
        <div className={styles.centered}>
          <Spinner label={t('common.loading')} />
        </div>
      </Layout>
    );
  }
  if (status !== 'authenticated') {
    return <Navigate to="/signin" replace />;
  }

  return (
    <Layout>
      <div className={styles.head}>
        <h1 className={styles.title}>{t('people.title')}</h1>
      </div>

      <Field
        label={t('people.searchLabel')}
        value={query}
        maxLength={30}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        autoFocus
        onChange={(event) => setQuery(event.target.value)}
      />

      {found !== null ? (
        found.length === 0 ? (
          <p className={styles.emptyText}>{t('people.noResults')}</p>
        ) : (
          <>
            <PersonList>
              {found.map((user) => (
                <PersonRow key={user.handle} person={user} />
              ))}
            </PersonList>
            {/*
              ⚠️ **조용히 자르지 않는다.** 쪽을 넘기는 대신(두 인덱스를 합치는
              구조라 커서가 둘이 된다) 잘렸다는 것만 알린다. 안 알리면 이게
              전부인 줄 알고 더 찾기를 포기한다 — 찾는 사람이 스물한 번째에
              있어도.
            */}
            {hasMore ? <p className={styles.searchMore}>{t('people.narrow')}</p> : null}
          </>
        )
      ) : null}
    </Layout>
  );
}
